/**
 * Phase 4 — slide-up pattern drawer.
 *
 * Triggered from the tactics-page HUD trailing slot. Lists saved
 * patterns with filter chips (tags, phase, formation). Selecting a
 * row fires `onSelectPattern(id)` — the parent surface decides what
 * to do (typically `recall_tactics_pattern` → `commitTacticsDraft`).
 *
 * The drawer is a controlled component: parent owns the open state
 * so the trigger button can live in the HUD without a context.
 */
import { useMemo, useState } from 'react';
import type {
  TacticsPattern,
  TacticsPatternPhase,
} from '../../lib/tacticsPatternsStore';
import { useTacticsPatterns } from '../../lib/queries/useTacticsPatterns';
import { HudChip } from './HudChip';
import './PatternDrawer.css';

interface PatternDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelectPattern: (id: string) => void;
  /** Optional callback so the surface can offer a Delete affordance. */
  onDeletePattern?: (id: string) => void;
}

const PHASE_CHIPS: ReadonlyArray<{ value: TacticsPatternPhase; label: string }> = [
  { value: 'attacking', label: 'Attacking' },
  { value: 'defending', label: 'Defending' },
  { value: 'transitions', label: 'Transitions' },
  { value: 'individual', label: 'Individual' },
];

export function PatternDrawer({
  open,
  onClose,
  onSelectPattern,
  onDeletePattern,
}: PatternDrawerProps) {
  const [phase, setPhase] = useState<TacticsPatternPhase | null>(null);
  const [formation, setFormation] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);

  // Pull the unfiltered library to derive the available tag + formation
  // chip universes, then re-filter on render. Cheap because the store
  // is localStorage-backed and the active library is tiny (tens of
  // patterns at most until v2 swaps in the DB layer).
  const { patterns: allPatterns, remove, refresh } = useTacticsPatterns();

  // Available filter dimensions — drawn from what the coach actually
  // has, not a hardcoded universe. Skips the formation chip row entirely
  // when only one formation is in play (no value chosen between equals).
  const { tagUniverse, formationUniverse } = useMemo(() => {
    const tagCounts = new Map<string, number>();
    const formations = new Set<string>();
    for (const p of allPatterns) {
      for (const t of p.tags) {
        const key = t.toLowerCase();
        tagCounts.set(key, (tagCounts.get(key) ?? 0) + 1);
      }
      formations.add(p.formation);
    }
    const tagU = Array.from(tagCounts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([t]) => t);
    return {
      tagUniverse: tagU,
      formationUniverse: Array.from(formations).sort(),
    };
  }, [allPatterns]);

  const filtered = useMemo(() => {
    return allPatterns.filter((p) => {
      if (phase && p.phaseOfPlay !== phase) return false;
      if (formation && p.formation !== formation) return false;
      if (activeTag) {
        const have = p.tags.map((t) => t.toLowerCase());
        if (!have.includes(activeTag)) return false;
      }
      return true;
    });
  }, [allPatterns, phase, formation, activeTag]);

  if (!open) return null;

  return (
    <div
      className="pattern-drawer"
      role="dialog"
      aria-label="Tactics pattern library"
      aria-modal="false"
    >
      <div className="pattern-drawer__header">
        <h2 className="pattern-drawer__title">Patterns</h2>
        <button
          type="button"
          className="pattern-drawer__close"
          onClick={onClose}
          aria-label="Close patterns drawer"
        >
          ×
        </button>
      </div>

      <div className="pattern-drawer__filters">
        <div className="pattern-drawer__filter-row" data-label="phase">
          <span className="pattern-drawer__filter-label">Phase</span>
          {PHASE_CHIPS.map((c) => (
            <HudChip
              key={c.value}
              active={phase === c.value}
              onClick={() => setPhase(phase === c.value ? null : c.value)}
            >
              {c.label}
            </HudChip>
          ))}
        </div>

        {formationUniverse.length > 1 && (
          <div className="pattern-drawer__filter-row" data-label="formation">
            <span className="pattern-drawer__filter-label">Formation</span>
            {formationUniverse.map((code) => (
              <HudChip
                key={code}
                active={formation === code}
                onClick={() => setFormation(formation === code ? null : code)}
              >
                {code}
              </HudChip>
            ))}
          </div>
        )}

        {tagUniverse.length > 0 && (
          <div className="pattern-drawer__filter-row" data-label="tags">
            <span className="pattern-drawer__filter-label">Tags</span>
            {tagUniverse.slice(0, 12).map((tag) => (
              <HudChip
                key={tag}
                active={activeTag === tag}
                onClick={() => setActiveTag(activeTag === tag ? null : tag)}
              >
                {tag}
              </HudChip>
            ))}
          </div>
        )}
      </div>

      <div className="pattern-drawer__list" role="list">
        {filtered.length === 0 && (
          <div className="pattern-drawer__empty">
            {allPatterns.length === 0
              ? 'No patterns saved yet. Build a layout and ask the Brain to "save this as Tuesday press".'
              : 'No patterns match the current filters.'}
          </div>
        )}
        {filtered.map((p) => (
          <PatternRow
            key={p.id}
            pattern={p}
            onSelect={() => onSelectPattern(p.id)}
            onDelete={
              onDeletePattern
                ? () => {
                    onDeletePattern(p.id);
                    remove(p.id);
                    refresh();
                  }
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}

interface PatternRowProps {
  pattern: TacticsPattern;
  onSelect: () => void;
  onDelete?: () => void;
}

function PatternRow({ pattern, onSelect, onDelete }: PatternRowProps) {
  return (
    <div className="pattern-drawer__row" role="listitem">
      <button
        type="button"
        className="pattern-drawer__row-main"
        onClick={onSelect}
        aria-label={`Apply pattern ${pattern.name}`}
      >
        <div className="pattern-drawer__row-title">{pattern.name}</div>
        <div className="pattern-drawer__row-meta">
          <span data-kind="phase">{pattern.phaseOfPlay}</span>
          <span data-kind="formation">{pattern.formation}</span>
          {pattern.sourceType === 'CURATED_EXPERT' && pattern.curatorName && (
            <span data-kind="curator">{pattern.curatorName}</span>
          )}
        </div>
        {pattern.tags.length > 0 && (
          <div className="pattern-drawer__row-tags">
            {pattern.tags.slice(0, 5).map((t) => (
              <span key={t} className="pattern-drawer__row-tag">
                {t}
              </span>
            ))}
          </div>
        )}
      </button>
      {onDelete && (
        <button
          type="button"
          className="pattern-drawer__row-delete"
          onClick={onDelete}
          aria-label={`Delete pattern ${pattern.name}`}
        >
          ×
        </button>
      )}
    </div>
  );
}
